import { computed } from 'vue'
import { createColumnHelper } from '@tanstack/vue-table'
import { useUserStore } from '@/stores'

const helper = createColumnHelper()

/**
 * Converts a flat column spec array into TanStack ColumnDef[], merging user
 * preferences (visibility, order, widths) from UserStore.
 *
 * @param {Array<{ id, label, size, type, sortField }>} columnSpecs
 * @param {string} prefsKey - UserStore settings key to read/write prefs (e.g. 'brandTableColumns')
 */
export const useColumns = (columnSpecs, prefsKey) => {
  const userStore = useUserStore()
  const prefs = computed(() => userStore.settings?.[prefsKey] ?? null)
  const defaultIds = columnSpecs.map(c => c.id)

  const columnDefs = computed(() => {
    const order = prefs.value?.order?.length ? prefs.value.order : defaultIds
    const widths = prefs.value?.widths ?? {}

    const ordered = order
      .map(id => columnSpecs.find(c => c.id === id))
      .filter(Boolean)
      .concat(columnSpecs.filter(c => !order.includes(c.id)))

    const defs = ordered.map(col => {
      // console.log('%cColumns', 'color:lime', col)
      return helper.accessor(row => row[col.id] ?? null, {
        id: col.id,
        header: col.label,
        size: widths[col.id] || col.size || 'grow',
        minSize: col.minSize || 16,
        maxSize: col.maxSize || Number.MAX_SAFE_INTEGER,
        enableSorting: !!col.sortField,
        meta: {
          type: col.type,
          sortField: col.sortField,
          component: col.component,
          align: col.align,
          tooltip: col.tooltip,
          ...col.meta
        }
      })
    })

    return defs
  })

  const initialVisibility = computed(() => {
    const hidden = prefs.value?.hidden
    if (!hidden) return {}
    return Object.fromEntries(columnSpecs.map(c => [c.id, !hidden.includes(c.id)]))
  })

  const initialOrder = computed(() => Array.isArray(prefs.value?.order) ? prefs.value.order : [])

  const savePrefs = newPrefs => {
    userStore.updateSetting(prefsKey, newPrefs)
  }

  return { columnDefs, initialVisibility, initialOrder, savePrefs }
}
